// src/components/HelpPanel.jsx
// Frequently asked questions, one set per role (student / marker / head
// marker), opened from the Help button in each dashboard's header (added
// 2026-11-02). Fixed text written for every tutor's platform: the tutor's
// own name is filled in, nothing here names Aileen or a specific course.
// Plain ASCII only (no curly quotes or dashes) - see CLAUDE.md on mojibake.

import React, { useMemo, useState } from 'react';
import { X, Search, ChevronDown, ChevronRight, HelpCircle } from 'lucide-react';

const studentFaqs = (tutor) => [
  {
    section: 'Submitting homework',
    items: [
      {
        q: 'How do I submit my homework?',
        a: [
          'On your dashboard, choose the homework from the list.',
          'If it asks for a PDF, select your PDF file (or several) and press Upload.',
          'If it says the homework should be completed in the homework app, press the start button. A homework window opens where you type your answers, then press Submit at the end.'
        ]
      },
      {
        q: "Why can't I submit?",
        a: [
          "Not open yet: the dashboard shows the date it opens.",
          `No deadline set: a homework can't be started or handed in until it has a deadline. Please contact ${tutor}.`,
          `Already submitted: it shows as "Already Submitted" in the list. If you need to change it, see "I submitted by mistake" below.`,
          `Account waiting for approval, or suspended: please contact ${tutor}.`
        ]
      },
      {
        q: 'I submitted by mistake or uploaded the wrong file. What do I do?',
        a: [
          'Uploaded PDF, not yet being marked (it shows as "Submitted"): delete the wrong file from your submission on your dashboard, then use Add File to upload the right one.',
          `Once it is being marked, or for homework typed in the homework app: contact ${tutor}. They can reopen typed homework so you can carry on editing with everything you wrote still there, give it back so you can start again, or move it to the right homework if you chose the wrong one.`
        ]
      },
      {
        q: 'I forgot a page. Can I add it after submitting?',
        a: [
          'Yes. Find the homework under your submissions on your dashboard and press Add File. If it still shows as "Submitted", the page is added straight away.',
          `If it is already being marked, the page is held for ${tutor} to check before it is added, so the marking is not disturbed.`,
          `For homework typed in the app, contact ${tutor}.`
        ]
      },
      {
        q: 'What can I upload?',
        a: [
          'PDF files. You can choose several in one go.',
          'Where the homework asks for it, you can also attach an Excel (.xlsx) spreadsheet.'
        ]
      },
      {
        q: 'What happens if I submit after the deadline?',
        a: [`You can still submit. It is marked as late and ${tutor} can see how late it was.`]
      }
    ]
  },
  {
    section: 'Homework typed in the app',
    items: [
      {
        q: 'Is it timed?',
        a: ['The start screen tells you whether it is a timed submission. Untimed homework can be done over several sittings.']
      },
      {
        q: 'What if I close the window or lose my connection?',
        a: [
          'Your answers save automatically as you type. Open the homework again from your dashboard to carry on where you left off.',
          'For timed homework the clock keeps running while the window is closed, and when time runs out what you have written is submitted automatically.'
        ]
      },
      {
        q: 'How does the spreadsheet work?',
        a: [
          'Much like Excel. Start a formula with = and click cells to add them to it. The toolbar has formatting, number formats, borders, copy and paste, and undo.',
          'The sheet grows as you work down it.'
        ]
      },
      {
        q: 'Where are the tax tables, calculator and scratch pad?',
        a: ['In the homework window. If your tutor provides tables or help notes, there is a Help button for them.']
      },
      {
        q: 'Can I keep a copy of what I submitted?',
        a: ['Yes. After you submit you can download your answers as a PDF, and your spreadsheets as an Excel file.']
      }
    ]
  },
  {
    section: 'After marking',
    items: [
      {
        q: 'How do I know where my homework is up to?',
        a: ['Your dashboard shows each homework as Submitted, Being Marked, or Marked with your score.']
      },
      {
        q: 'Where is my marked homework and feedback?',
        a: ['On your dashboard, open the marked homework to view the marked PDF. If your spreadsheet was marked, you can view the marks on it there too.']
      },
      {
        q: 'When can I see the solutions?',
        a: [
          `If ${tutor} shares solutions in the app, a solution button appears next to your marked homework, either once the deadline has passed or as soon as your homework is marked (${tutor} chooses which).`,
          `If there is no solution button, ${tutor} may share solutions another way, for example in class or by email. Please ask them.`
        ]
      },
      {
        q: 'Can I delete a marked homework?',
        a: [`Yes, from your dashboard. ${tutor} still keeps a record of it.`]
      }
    ]
  },
  {
    section: 'Your account',
    items: [
      {
        q: 'I have more than one tutor. How do I add or switch between them?',
        a: ["Use Add tutor on your dashboard. Type the tutor's name and the cohort code they gave you. You can then switch between your tutors there."]
      },
      {
        q: "I forgot my password, or didn't get my verification email.",
        a: ['On the login page, use Forgot password. If your email needs verifying, the login page offers to resend the email. Check your spam folder too.']
      },
      {
        q: 'Can I use my phone?',
        a: ['Yes for your dashboard and PDF uploads. Homework typed in the app is much easier on a computer.']
      },
      {
        q: 'What is Course Content?',
        a: ['If you have a course account, the Course Content button takes you straight to your course without logging in again.']
      }
    ]
  }
];

const markerFaqs = (tutor) => [
  {
    section: 'Getting work',
    items: [
      {
        q: 'How do I claim a homework?',
        a: ['Press Claim on a homework in the available list. It moves to your own list.']
      },
      {
        q: "I can't mark something I claimed. Can I give it back?",
        a: ['Yes, unclaim it from your list and it goes back for someone else.']
      },
      {
        q: 'How do I get emails about new submissions?',
        a: [
          'Open the Notifications tab on your dashboard and tick each homework you want an email for.',
          `If you have ticked a homework but get no emails, ${tutor} may have new-submission emails switched off for markers. Ask them.`
        ]
      }
    ]
  },
  {
    section: 'Marking',
    items: [
      {
        q: 'How do I mark?',
        a: [
          'Open the homework to go to the marking screen. Add marks and comments on the pages, and use the comment bank for comments you use often.',
          'Where a spreadsheet is marked interactively, you can put marks on its cells as well.'
        ]
      },
      {
        q: 'How do I save and finish?',
        a: ['Save Progress & Come Back Later keeps your work so far. Complete Marking finishes it.']
      },
      {
        q: 'Why should I use Chrome on a computer?',
        a: ['The marking screen is built for Chrome on a computer. It may not work properly in other browsers or on a phone.']
      },
      {
        q: "There's a problem with a homework (missing pages, wrong file).",
        a: [`Flag it for review with a note. It goes to ${tutor}, who sorts it out and sends it back to you.`]
      },
      {
        q: 'The student added pages after I started marking.',
        a: [`New pages are held for ${tutor} to check first, so your marking is not disturbed. Once released they are added to the homework.`]
      },
      {
        q: 'How do I suggest a new comment for the comment bank?',
        a: [`Suggest it from the comment bank on the marking screen. ${tutor} approves it before it is added.`]
      }
    ]
  },
  {
    section: 'After marking',
    items: [
      {
        q: 'What does "awaiting review" mean?',
        a: [`For some homeworks ${tutor} checks the marking before it goes to the student.`]
      },
      {
        q: 'A homework came back to me as "returned for revision".',
        a: [`${tutor} has sent it back with a note, either from their review or after it was marked. Your marking is kept. Make the changes and press Complete Marking again. It is not counted twice in your stats.`]
      },
      {
        q: 'I finished marking by mistake.',
        a: [`Ask ${tutor} to send it back to you. Your marking is kept.`]
      },
      {
        q: 'Where are my stats?',
        a: [`On your dashboard. ${tutor} may reset the counts, for example after paying for the work, and keeps a record of each reset.`]
      }
    ]
  }
];

const headMarkerFaqs = () => [
  {
    section: 'Students',
    items: [
      {
        q: 'How do students register?',
        a: [
          'They sign up with your registration link and the cohort code (Cohorts tab).',
          'In Settings > Account you can require email verification, and choose whether new students or students adding you as a tutor need your approval. Approve them in Cohorts, under recent registrations.'
        ]
      },
      {
        q: 'How do I move, suspend, remove a student or give extra time?',
        a: ['Cohorts tab: open the cohort, then the student. The buttons are at the top of their page.']
      },
      {
        q: 'A student submitted by mistake or needs to change their homework.',
        a: [
          'Uploaded PDF, not yet claimed: the student can delete files and use Add File on their submission themselves. Nothing for you to do.',
          'Uploaded PDF, already claimed or marked, and the student adds a page: it does not go straight in. It waits in Issues > Pending Additions. Check it and press release: the page is added to the end of the file (marking already done is unaffected) and the homework moves to Flagged for Review. Send it back to the marker from there to mark the new page.',
          'Typed in the app, untimed and not yet claimed: press the blue pencil (Reopen) in Available. Everything they typed is kept, the submission is set aside (not deleted), and they submit again when ready. Tell the student it is open.',
          "Needs to start again: press the orange arrow (Reset) in Available or Claimed. This deletes their files and any marking and emails the student to submit again. Typed homework starts blank.",
          'Right work, wrong homework: use Change type in Available. The student is not told.',
          'Student cannot upload it themselves (for example, they sent it to you by email): Upload Submission for Student in Available.'
        ]
      },
      {
        q: 'A marker finished marking by mistake, or the marking needs fixing.',
        a: [
          'Still awaiting review: press Reject in the Review Queue with a note.',
          "Already marked: Cohorts tab, open the student, then Send back to marker on that homework. Add a note. It goes back to the marker with their marking kept, comes off the student's dashboard until finished again, and is not counted twice in their stats."
        ]
      },
      {
        q: 'What is in the Issues tab?',
        a: [
          'Flagged for Review: homework a marker has flagged with a problem. Send it back to the marker or release it to the pool.',
          'Pending Additions: pages a student uploaded after marking had started. Check and release them.'
        ]
      }
    ]
  },
  {
    section: 'Homework setup',
    items: [
      {
        q: 'How do I set deadlines and opening dates?',
        a: [
          'Cohorts tab > Deadlines. Every homework needs a deadline before students can start or submit it. An opening date is optional and hides the homework until then.',
          'You can also choose which homeworks each cohort does there.'
        ]
      },
      {
        q: 'How do I set up a homework typed in the app?',
        a: ['Settings > Homework Settings: choose exam mode, the time allowed or untimed, the question structure, exhibits and requirements, and the help or tax tables document.']
      },
      {
        q: 'Which homeworks go through review, and when are solutions released?',
        a: ['Settings > Homework Settings, per homework: whether marking is reviewed before it reaches the student, and whether the solution waits for the deadline or is released as soon as the homework is marked.']
      },
      {
        q: 'How does spreadsheet marking work?',
        a: ['Turn on interactive spreadsheet marking for a homework in Settings > Homework Settings. Markers then put marks straight on the cells, and the marked spreadsheet is added to the marked PDF.']
      }
    ]
  },
  {
    section: 'Marking team',
    items: [
      {
        q: 'How do I add or remove markers and see their progress?',
        a: ['Markers tab: Activity shows what each marker is working on; Manage Markers is for adding, deactivating and stats.']
      },
      {
        q: 'Where is the comment bank?',
        a: ['Settings > Comment Library. Comments markers suggest wait there for your approval.']
      },
      {
        q: 'Reminders and notification emails',
        a: [
          'Student deadline reminders are in Cohorts > Deadlines.',
          "Your own notification emails are in Settings > Notifications. That is also where you switch new-submission emails to markers on or off for your whole team. Each marker then ticks the homeworks they want in their own Notifications tab."
        ]
      }
    ]
  },
  {
    section: 'Results and account',
    items: [
      {
        q: 'Where are the results?',
        a: ['Cohorts tab > Results.']
      },
      {
        q: 'What is support access?',
        a: ['Settings > Account. It lets platform support log in as you to help with a problem, either always or for 24 hours at a time. Leave it off unless you need help.']
      }
    ]
  }
];

const FAQS = { student: studentFaqs, marker: markerFaqs, head_marker: headMarkerFaqs };

function HelpPanel({ role, tutorName, onClose }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState({});
  const tutor = tutorName && tutorName !== 'the head marker' ? tutorName : 'your tutor';
  const sections = useMemo(() => (FAQS[role] || FAQS.student)(tutor), [role, tutor]);

  const term = search.trim().toLowerCase();
  const filtered = sections
    .map(s => ({
      ...s,
      items: term
        ? s.items.filter(i => (i.q + ' ' + i.a.join(' ')).toLowerCase().includes(term))
        : s.items
    }))
    .filter(s => s.items.length > 0);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)', height: '100%', background: 'white', display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.2)', fontFamily: 'inherit'
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <HelpCircle size={22} color="#c33329" />
          <div style={{ flex: 1, fontSize: '18px', fontWeight: '700', color: '#2c3e50' }}>Help and FAQs</div>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
          >
            <X size={20} color="#4a5568" />
          </button>
        </div>

        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #cbd5e0', borderRadius: '8px', padding: '8px 10px' }}>
            <Search size={16} color="#718096" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the questions"
              style={{ border: 'none', outline: 'none', flex: 1, fontSize: '14px' }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 24px' }}>
          {filtered.length === 0 && (
            <p style={{ color: '#718096', fontSize: '14px', marginTop: '16px' }}>
              Nothing matches that. {role === 'head_marker' ? '' : `Please contact ${tutor}.`}
            </p>
          )}
          {filtered.map(s => (
            <div key={s.section} style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                {s.section}
              </div>
              {s.items.map(item => {
                const key = s.section + '|' + item.q;
                const isOpen = !!term || !!open[key];
                return (
                  <div key={key} style={{ borderBottom: '1px solid #edf2f7' }}>
                    <button
                      type="button"
                      onClick={() => setOpen(prev => ({ ...prev, [key]: !prev[key] }))}
                      style={{
                        width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
                        padding: '10px 0', display: 'flex', alignItems: 'flex-start', gap: '8px',
                        fontSize: '15px', fontWeight: '600', color: '#2c3e50'
                      }}
                    >
                      {isOpen ? <ChevronDown size={18} style={{ flexShrink: 0, marginTop: '1px' }} /> : <ChevronRight size={18} style={{ flexShrink: 0, marginTop: '1px' }} />}
                      {item.q}
                    </button>
                    {isOpen && (
                      <div style={{ padding: '0 0 12px 26px' }}>
                        {item.a.map((para, i) => (
                          <p key={i} style={{ margin: '0 0 8px', fontSize: '14px', lineHeight: 1.5, color: '#4a5568' }}>{para.charAt(0).toUpperCase() + para.slice(1)}</p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HelpPanel;
